import { Box, Card, Dialog, Stack } from '@mui/material';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  TextField,
  useTranslate,
  FunctionField,
  BooleanField,
  useNotify,
  NumberField,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getLottoDrawConfigs,
  createLottoDrawConfig,
  updateLottoDrawConfig,
  LottoDrawConfig,
} from '../../../services/redpacketService';
import CreateLottoDrawConfig from './Create';
import EditLottoDrawConfig from './Edit';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const LottoDrawConfigsList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [data, setData] = useState<LottoDrawConfig[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getLottoDrawConfigs();
      setData(Array.isArray(result) ? result : []);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.lottoDrawConfigs')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.lottoDrawConfigs') },
          ]}
          action={
            <PermissionGuard resource="redpacket" action="create" fallback={<></>}>
              <CreateButton
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  setPopup(
                    <CreateLottoDrawConfig
                      onClose={() => {
                        setPopup(undefined);
                        fetchData();
                      }}
                    />
                  );
                }}
              />
            </PermissionGuard>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid rowClick={false} data={data} isLoading={loading} bulkActionButtons={false}>
              <FunctionField
                label={translate('module.redpacket.drawTimeUtc8Col')}
                render={(record: LottoDrawConfig) => {
                  const utcTime = record.drawTimeUtc;
                  if (!utcTime) return '-';
                  const utc8 = dayjs.utc(`2000-01-01T${utcTime}:00`).tz('Asia/Shanghai');
                  return utc8.format('HH:mm');
                }}
              />
              <NumberField source="prizeAmount" label={translate('module.redpacket.prizeAmountCol')} />
              <NumberField source="totalBudget" label={translate('module.redpacket.totalBudgetCol')} />
              <NumberField source="remainingBudget" label={translate('module.redpacket.remainingBudgetCol')} />
              <TextField source="fundPoolAddress" label={translate('module.redpacket.fundPoolAddressCol')} />
              <FunctionField
                label={translate('module.redpacket.status')}
                render={(record: LottoDrawConfig) => (
                  <Chip
                    label={record.isActive ? translate('resource.active') : translate('resource.inactive')}
                    color={record.isActive ? 'success' : 'error'}
                    size="small"
                  />
                )}
              />
              <NumberField source="eligibilityThreshold" label={translate('module.redpacket.eligibilityThresholdCol')} />
              <FunctionField
                label={translate('module.redpacket.updatedAtCol')}
                render={(record: LottoDrawConfig) => {
                  const date = record.updatedAt;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <FunctionField
                label={translate('module.redpacket.operation')}
                render={(record: LottoDrawConfig) => (
                  <Stack direction="row" spacing={1}>
                    <PermissionButton resource="redpacket" action="update">
                      <EditButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopup(
                            <EditLottoDrawConfig
                              id={record.id}
                              record={record}
                              onClose={() => {
                                setPopup(undefined);
                                fetchData();
                              }}
                            />
                          );
                        }}
                      />
                    </PermissionButton>
                  </Stack>
                )}
              />
            </Datagrid>
          </Box>
        </Card>

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default LottoDrawConfigsList;
